/**
 * Settings Page
 * 
 * Application settings and configuration with full functionality.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import {
  Cloud,
  CreditCard,
  Users,
  Webhook,
  Save,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Check,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { profileApi, gpuJobsApi } from '../lib/api';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

// Toggle Switch Component
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ enabled, onChange, disabled = false }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={clsx(
        'relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gpu-500 focus:ring-offset-2 focus:ring-offset-surface-900',
        enabled ? 'bg-gpu-500' : 'bg-surface-700',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={clsx(
          'absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transform transition-transform duration-200 ease-in-out',
          enabled ? 'translate-x-7' : 'translate-x-1'
        )}
      />
    </button>
  );
}

type TabType = 'instance' | 'cost' | 'webhooks' | 'team' | 'danger';

export default function SettingsPage() {
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('instance');

  // Instance settings state
  const [instanceSettings, setInstanceSettings] = useState({
    defaultInstanceType: 'g4dn.xlarge',
    defaultMaxRuntime: '4',
    defaultDockerImage: '',
    preferredRegion: 'us-east-1',
    useSpotInstances: true,
  });

  // Cost management state
  const [costSettings, setCostSettings] = useState({
    monthlySpendingLimit: 500,
    perJobCostLimit: 50,
    autoTerminateIdle: '30',
  });

  // Webhook settings state
  const [webhookSettings, setWebhookSettings] = useState({
    webhookUrl: '',
    events: {
      jobCreated: false,
      jobStarted: false,
      jobCompleted: false,
      jobFailed: false,
      spendingAlert: false,
      apiKeyCreated: false,
    },
  });

  // Team settings state
  const [teamSettings, setTeamSettings] = useState({
    allowMembersCreateJobs: true,
    requireApprovalLargeJobs: false,
  });

  // Delete confirmation state
  const [showDeleteJobsConfirm, setShowDeleteJobsConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteJobsConfirmText, setDeleteJobsConfirmText] = useState('');
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState('');
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');

  // Delete all jobs mutation
  const deleteAllJobsMutation = useMutation({
    mutationFn: async () => {
      const jobs = await gpuJobsApi.list();
      if (jobs.length === 0) {
        throw new Error('No jobs to delete');
      }
      const results = await Promise.allSettled(
        jobs.map((job) => gpuJobsApi.remove(job.id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        throw new Error(`Failed to delete ${failed} of ${jobs.length} jobs`);
      }
      return { count: jobs.length };
    },
    onSuccess: (data) => {
      toast.success(`All ${data.count} jobs have been deleted`);
      setShowDeleteJobsConfirm(false);
      setDeleteJobsConfirmText('');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete jobs');
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: (password: string) => profileApi.deleteAccount(password),
    onSuccess: () => {
      toast.success('Account deleted successfully');
      window.location.href = '/login';
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete account');
    },
  });

  const markChanged = () => {
    if (!hasChanges) setHasChanges(true);
  };

  const handleInstanceSettingChange = <K extends keyof typeof instanceSettings>(
    key: K,
    value: typeof instanceSettings[K]
  ) => {
    setInstanceSettings(prev => ({ ...prev, [key]: value }));
    markChanged();
  };

  const handleCostSettingChange = <K extends keyof typeof costSettings>(
    key: K,
    value: typeof costSettings[K]
  ) => {
    setCostSettings(prev => ({ ...prev, [key]: value }));
    markChanged();
  };

  const handleWebhookEventToggle = (event: keyof typeof webhookSettings.events) => {
    setWebhookSettings(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [event]: !prev.events[event],
      },
    }));
    markChanged();
  };

  const handleTeamSettingToggle = (key: keyof typeof teamSettings) => {
    setTeamSettings(prev => ({ ...prev, [key]: !prev[key] }));
    markChanged();
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In production, save to backend
      // await settingsApi.save({ instanceSettings, costSettings, webhookSettings, teamSettings });
      
      toast.success('Settings saved successfully');
      setHasChanges(false);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAllJobs = () => {
    if (deleteJobsConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    deleteAllJobsMutation.mutate();
  };

  const handleDeleteAccount = () => {
    if (deleteAccountConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }
    if (!deleteAccountPassword) {
      toast.error('Please enter your password to confirm');
      return;
    }
    deleteAccountMutation.mutate(deleteAccountPassword);
  };

  const webhookEvents = [
    { key: 'jobCreated' as const, label: 'Job Created' },
    { key: 'jobStarted' as const, label: 'Job Started' },
    { key: 'jobCompleted' as const, label: 'Job Completed' },
    { key: 'jobFailed' as const, label: 'Job Failed' },
    { key: 'spendingAlert' as const, label: 'Spending Alert' },
    { key: 'apiKeyCreated' as const, label: 'API Key Created' },
  ];

  const tabs = [
    { id: 'instance' as const, label: 'Instance Configuration', icon: Cloud },
    { id: 'cost' as const, label: 'Cost Management', icon: CreditCard },
    { id: 'webhooks' as const, label: 'Webhooks', icon: Webhook },
    { id: 'team' as const, label: 'Team Settings', icon: Users },
    { id: 'danger' as const, label: 'Danger Zone', icon: AlertTriangle },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-4xl mx-auto space-y-8"
    >
      {/* Header */}
      <motion.header variants={itemVariants} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white mb-2">
            Settings
          </h1>
          <p className="text-sm sm:text-base text-surface-400">
            Configure your account and application preferences
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        )}
      </motion.header>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center gap-1 p-1 bg-surface-900/50 rounded-xl border border-surface-800/50 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap',
                activeTab === id
                  ? 'bg-surface-800 text-white shadow-sm'
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* Instance Configuration Tab */}
        {activeTab === 'instance' && (
          <motion.div
            key="instance"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.section className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-surface-400" />
                Default Instance Configuration
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Default Instance Type</label>
                  <select
                    value={instanceSettings.defaultInstanceType}
                    onChange={(e) => handleInstanceSettingChange('defaultInstanceType', e.target.value)}
                    className="input"
                  >
                    <option value="g4dn.xlarge">g4dn.xlarge (1x T4, 16GB)</option>
                    <option value="g4dn.2xlarge">g4dn.2xlarge (1x T4, 32GB)</option>
                    <option value="g5.xlarge">g5.xlarge (1x A10G, 24GB)</option>
                    <option value="g5.2xlarge">g5.2xlarge (1x A10G, 32GB)</option>
                    <option value="p3.2xlarge">p3.2xlarge (1x V100, 16GB)</option>
                    <option value="p4d.24xlarge">p4d.24xlarge (8x A100, 320GB)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Default Max Runtime</label>
                  <select
                    value={instanceSettings.defaultMaxRuntime}
                    onChange={(e) => handleInstanceSettingChange('defaultMaxRuntime', e.target.value)}
                    className="input"
                  >
                    <option value="1">1 hour</option>
                    <option value="2">2 hours</option>
                    <option value="4">4 hours</option>
                    <option value="8">8 hours</option>
                    <option value="24">24 hours</option>
                    <option value="48">48 hours</option>
                    <option value="72">72 hours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Default Docker Image</label>
                  <input
                    type="text"
                    value={instanceSettings.defaultDockerImage}
                    onChange={(e) => handleInstanceSettingChange('defaultDockerImage', e.target.value)}
                    className="input"
                    placeholder="pytorch/pytorch:2.0.0-cuda11.7-cudnn8-runtime"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Preferred Region</label>
                  <select
                    value={instanceSettings.preferredRegion}
                    onChange={(e) => handleInstanceSettingChange('preferredRegion', e.target.value)}
                    className="input"
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-east-2">US East (Ohio)</option>
                    <option value="us-west-1">US West (N. California)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={instanceSettings.useSpotInstances}
                    onChange={(e) => handleInstanceSettingChange('useSpotInstances', e.target.checked)}
                    className="w-4 h-4 rounded bg-surface-800 border-surface-600 text-gpu-500 focus:ring-gpu-500 focus:ring-offset-surface-900"
                  />
                  <span className="text-sm text-surface-300">Use Spot Instances by default (save up to 70%)</span>
                </label>
              </div>
            </motion.section>
          </motion.div>
        )}

        {/* Cost Management Tab */}
        {activeTab === 'cost' && (
          <motion.div
            key="cost"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.section className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-surface-400" />
                Cost Management
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Monthly Spending Limit</p>
                    <p className="text-xs text-surface-500 mt-0.5">Get alerted when spending approaches this limit</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-surface-400">$</span>
                    <input
                      type="number"
                      value={costSettings.monthlySpendingLimit}
                      onChange={(e) => handleCostSettingChange('monthlySpendingLimit', parseInt(e.target.value) || 0)}
                      className="input w-28"
                      min={0}
                      step={50}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Per-Job Cost Limit</p>
                    <p className="text-xs text-surface-500 mt-0.5">Prevent jobs from exceeding this estimated cost</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-surface-400">$</span>
                    <input
                      type="number"
                      value={costSettings.perJobCostLimit}
                      onChange={(e) => handleCostSettingChange('perJobCostLimit', parseInt(e.target.value) || 0)}
                      className="input w-28"
                      min={0}
                      step={10}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Auto-terminate Idle Jobs</p>
                    <p className="text-xs text-surface-500 mt-0.5">Terminate jobs with no GPU activity after</p>
                  </div>
                  <select
                    value={costSettings.autoTerminateIdle}
                    onChange={(e) => handleCostSettingChange('autoTerminateIdle', e.target.value)}
                    className="input w-40"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
            </motion.section>
          </motion.div>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && (
          <motion.div
            key="webhooks"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.section className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Webhook className="w-5 h-5 text-surface-400" />
                Webhooks
              </h3>
              <p className="text-sm text-surface-400 mb-4">
                Receive real-time notifications when events occur in your account.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Webhook URL</label>
                  <input
                    type="url"
                    value={webhookSettings.webhookUrl}
                    onChange={(e) => {
                      setWebhookSettings(prev => ({ ...prev, webhookUrl: e.target.value }));
                      markChanged();
                    }}
                    className="input"
                    placeholder="https://your-server.com/webhook"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Events to Send</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {webhookEvents.map((event) => (
                      <label key={event.key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={webhookSettings.events[event.key]}
                          onChange={() => handleWebhookEventToggle(event.key)}
                          className="w-4 h-4 rounded bg-surface-800 border-surface-600 text-gpu-500 focus:ring-gpu-500 focus:ring-offset-surface-900"
                        />
                        <span className="text-sm text-surface-300">{event.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {webhookSettings.webhookUrl && (
                  <button
                    onClick={() => {
                      toast.success('Test webhook sent!');
                    }}
                    className="btn-secondary text-sm"
                  >
                    Send Test Webhook
                  </button>
                )}
              </div>
            </motion.section>
          </motion.div>
        )}

        {/* Team Settings Tab */}
        {activeTab === 'team' && (
          <motion.div
            key="team"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.section className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Users className="w-5 h-5 text-surface-400" />
                Team Settings
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Allow Team Members to Create Jobs</p>
                    <p className="text-xs text-surface-500 mt-0.5">Team members can launch GPU instances</p>
                  </div>
                  <ToggleSwitch
                    enabled={teamSettings.allowMembersCreateJobs}
                    onChange={() => handleTeamSettingToggle('allowMembersCreateJobs')}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Require Approval for Large Jobs</p>
                    <p className="text-xs text-surface-500 mt-0.5">Jobs over $10/hr require admin approval</p>
                  </div>
                  <ToggleSwitch
                    enabled={teamSettings.requireApprovalLargeJobs}
                    onChange={() => handleTeamSettingToggle('requireApprovalLargeJobs')}
                  />
                </div>
              </div>
            </motion.section>
          </motion.div>
        )}

        {/* Danger Zone Tab */}
        {activeTab === 'danger' && (
          <motion.div
            key="danger"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.section className="bg-red-950/20 border border-red-900/30 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-red-400 mb-6 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </h3>
              <div className="space-y-4">
                {/* Delete All Jobs */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-900/50 border border-red-900/20">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Delete All Jobs</p>
                    <p className="text-xs text-surface-500 mt-0.5">Permanently delete all job history and data</p>
                  </div>
                  <button
                    onClick={() => setShowDeleteJobsConfirm(true)}
                    disabled={deleteAllJobsMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteAllJobsMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {deleteAllJobsMutation.isPending ? 'Deleting...' : 'Delete All'}
                  </button>
                </div>

                {/* Delete Jobs Confirmation */}
                {showDeleteJobsConfirm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                  >
                    <p className="text-sm text-surface-300 mb-3">
                      This action <span className="text-red-400 font-semibold">cannot be undone</span>. Type{' '}
                      <code className="px-1 py-0.5 bg-surface-800 rounded text-red-400">DELETE</code> to confirm.
                    </p>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={deleteJobsConfirmText}
                        onChange={(e) => setDeleteJobsConfirmText(e.target.value)}
                        className="input flex-1"
                        placeholder="Type DELETE to confirm"
                        disabled={deleteAllJobsMutation.isPending}
                      />
                      <button
                        onClick={handleDeleteAllJobs}
                        disabled={deleteJobsConfirmText !== 'DELETE' || deleteAllJobsMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                      >
                        {deleteAllJobsMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                        {deleteAllJobsMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteJobsConfirm(false);
                          setDeleteJobsConfirmText('');
                        }}
                        disabled={deleteAllJobsMutation.isPending}
                        className="btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Delete Account */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-900/50 border border-red-900/20">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Delete Account</p>
                    <p className="text-xs text-surface-500 mt-0.5">Permanently delete your account and all data</p>
                  </div>
                  <button
                    onClick={() => setShowDeleteAccountConfirm(true)}
                    disabled={deleteAccountMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteAccountMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>

                {/* Delete Account Confirmation */}
                {showDeleteAccountConfirm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                  >
                    <p className="text-sm text-surface-300 mb-3">
                      This will <span className="text-red-400 font-semibold">permanently delete</span> your account,
                      all jobs, API keys, and data. Type{' '}
                      <code className="px-1 py-0.5 bg-surface-800 rounded text-red-400">DELETE MY ACCOUNT</code> to confirm.
                    </p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={deleteAccountConfirmText}
                        onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                        className="input w-full"
                        placeholder="Type DELETE MY ACCOUNT to confirm"
                        disabled={deleteAccountMutation.isPending}
                      />
                      <input
                        type="password"
                        value={deleteAccountPassword}
                        onChange={(e) => setDeleteAccountPassword(e.target.value)}
                        className="input w-full"
                        placeholder="Enter your password"
                        disabled={deleteAccountMutation.isPending}
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deleteAccountConfirmText !== 'DELETE MY ACCOUNT' || !deleteAccountPassword || deleteAccountMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                        >
                          {deleteAccountMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                          {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Forever'}
                        </button>
                        <button
                          onClick={() => {
                            setShowDeleteAccountConfirm(false);
                            setDeleteAccountConfirmText('');
                            setDeleteAccountPassword('');
                          }}
                          disabled={deleteAccountMutation.isPending}
                          className="btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Button */}
      <motion.div variants={itemVariants} className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={isSaving || !hasChanges}
          className={clsx(
            'btn-primary',
            !hasChanges && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isSaving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : hasChanges ? (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              All Saved
            </>
          )}
        </button>
      </motion.div>
    </motion.div>
  );
}
